import React, {useEffect, useRef, useState, useMemo} from "react";
import styles from "../../styles/dashboard.module.css";
import Header from "../../components/header/header";
import axiosInstance from "../../AxiosInstance";
import {useTranslation} from "react-i18next";
import DeviceBox from "../../components/boxes/device";
import VariableBox from "../../components/boxes/variable";
import EmptyPlaceholder from "../../components/empty_placeholder/empty_placeholder";
import Modal from "../../components/modal/modal";
import {LogoInput, SelectInput, SliderInput, TextInput} from "../../components/input/inputs";
import GridLayout from 'react-grid-layout';
import DashboardGraphic from "./graphic/graphic";
import {fa1, fa2, fa3, faCog, faEdit, faLock, faLockOpen, faPlus, faTableCells, faTrash} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Label, Select} from "../../components/ajonjolib/inputs/ajonjolinput";
import VerticalInput from "../../components/vertical_input/vertical_input";
import useMQTTMultiDevice from "../../components/mqtt_service/mqtt_multi_device";
import BobWidget from "../../components/boxes/bob_widget";
import AlarmTableWidget from "../../components/boxes/alarm_table_widget";
import DashboardProductionHistogram from "./production_histogram/production_histogram";
import GaugeWidget from "../../components/boxes/gauge_widget";
import ButtonWidget from "../../components/boxes/button_widget";
import {countAlertStates} from "../../utils/alertCounts";
import {getStateLabels} from "../../components/utils";

function ActionPopup({widget_pk, widgetData, setShowPopup, setShowMachineModal, setShowVariableModal, setShowDeviceModal, setJoinGraphModal, setShowAlarmTableModal, onOpenAlarmTableSettings}) {
    const {t} = useTranslation();

    const deleteWidget = () => {
        axiosInstance.delete(`dashboard_widget/${widget_pk}/`).then((response) => {
            window.location.reload();
        });
    }

    const openSettings = () => {
        setShowPopup(false);
        if(widgetData.type === 'machine') {
            setShowMachineModal(true);
        } else if(widgetData.type === 'device') {
            setShowDeviceModal(true);
        } else if(widgetData.type === 'variable') {
            setShowVariableModal(true);
        } else if(widgetData.type === 'graph') {
            setJoinGraphModal(true);
        } else if(widgetData.type === 'alarm_table') {
            if (onOpenAlarmTableSettings) onOpenAlarmTableSettings(widgetData.data);
            setShowAlarmTableModal(true);
        }
    }

    const hasSettings = ['machine', 'device', 'variable', 'graph', 'alarm_table'].includes(widgetData.type);

    return (
        <div style={{width: '200px', height: 'auto'}}>
            {hasSettings && (
                <div className={styles.popupButton} onClick={() => openSettings()}>
                    <FontAwesomeIcon icon={faCog} color={'#64748B'} className={'me-2'}/>
                    <div style={{color: '#334155'}}>{t('dashboard.settings')}</div>
                </div>
            )}
            <div className={styles.popupButton} onClick={() => deleteWidget()}>
                <FontAwesomeIcon icon={faTrash} color={'#f64a4a'} className={'me-2'}/>
                <div style={{color: '#f64a4a'}}>{t('common.delete')}</div>
            </div>
        </div>
    )
}

export default function Dashboard({data}) {
    const {t} = useTranslation();
    const dashboard_id = parseInt(localStorage.getItem('dashboard_id'));
    const project_id = parseInt(localStorage.getItem('project_id'));
    const [widgets, setWidgets] = useState({});
    const [showGraphicsModal, setShowGraphicsModal] = useState(false);
    const [form, setForm] = useState({});
    const [selectedGraph, setSelectedGraph] = useState(null);
    const [availableSpace, setAvailableSpace] = useState(0);
    const [availableCols, setAvailableCols] = useState(1);
    const spaceRef = useRef(null)
    const [joinGraphModal, setJoinGraphModal] = useState(false);
    const [layout, setLayout] = useState([]);
    const [initialLayout, setInitialLayout] = useState([]);
    const [hasLayoutChanges, setHasLayoutChanges] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const popupRef = useRef(null);
    const [mousePosition, setMousePosition] = useState({x: 0, y: 0});
    const [selectedWidget, setSelectedWidget] = useState(null);

    const [machineOptions, setMachineOptions] = useState([]);

    const [showMachineModal, setShowMachineModal] = useState(false);
    const [showDeviceModal, setShowDeviceModal] = useState(false);
    const [showVariableModal, setShowVariableModal] = useState(false);
    const [widgetData, setWidgetData] = useState({});
    const [resultVariables, setResultVariables] = useState([]);
    const [editMode, setEditMode] = useState(false);

    const [showAlarmTableModal, setShowAlarmTableModal] = useState(false);
    const [alarmTableDevices, setAlarmTableDevices] = useState([]);
    const [alarmTableSelectedDevice, setAlarmTableSelectedDevice] = useState(null);
    const [alarmTableVariables, setAlarmTableVariables] = useState([]);
    const [alarmTableSelectedVarIds, setAlarmTableSelectedVarIds] = useState([]);
    const [alarmTableName, setAlarmTableName] = useState('');
    const [editingAlarmTableWidgetPk, setEditingAlarmTableWidgetPk] = useState(null);

    // Extraer device IDs únicos de los widgets para suscribirse via MQTT.
    // Se memoiza sobre el CONJUNTO (una clave string ordenada) y no sobre el
    // array: el efecto que refresca los badges reescribe widgets.devices en cada
    // mensaje, así que un array nuevo cada vez metería a useMQTTMultiDevice en
    // un bucle de subscribe/unsubscribe.
    const deviceIdsKey = useMemo(() => {
        const ids = new Set();
        widgets.variables?.forEach(v => {
            if (v.data?.device) ids.add(v.data.device);
        });
        widgets.devices?.forEach(d => {
            if (d.data?.id) ids.add(d.data.id);
        });
        widgets.alarm_tables?.forEach(at => {
            if (at.device?.id) ids.add(at.device.id);
        });
        widgets.gauges?.forEach(g => {
            if (g.data?.device) ids.add(g.data.device);
        });
        // Widgets de máquina: hay que suscribirse a cada dispositivo hijo para
        // poder rehacer el roll-up del badge en vivo. `children` lo envía el
        // endpoint del dashboard.
        widgets.machines?.forEach(m => (m.children || []).forEach(c => {
            if (c?.id) ids.add(c.id);
        }));
        return Array.from(ids).sort((a, b) => a - b).join(',');
    }, [widgets.variables, widgets.devices, widgets.alarm_tables, widgets.gauges, widgets.machines]);

    const deviceIds = useMemo(
        () => (deviceIdsKey ? deviceIdsKey.split(',').map(Number) : []),
        [deviceIdsKey]);

    // Suscribirse a MQTT para actualizaciones en tiempo real (incluye topic de gráficos y variables calculadas)
    const { data: mqttData, graphData, customVarData, getVariableById, clearGraphData, clearCustomVarData } = useMQTTMultiDevice(deviceIds, "myindustrialdata", true, project_id);

    // Actualizar widgets de variables cuando llegan datos por MQTT
    useEffect(() => {
        if (!mqttData || Object.keys(mqttData).length === 0) return;

        setWidgets(prevWidgets => {
            if (!prevWidgets.variables) return prevWidgets;

            const updatedVariables = prevWidgets.variables.map(variable => {
                const updatedData = getVariableById(variable.data?.id);
                if (updatedData) {
                    return {
                        ...variable,
                        data: updatedData
                    };
                }
                return variable;
            });

            // Solo actualizar si hay cambios
            const hasChanges = updatedVariables.some((v, i) =>
                v.data !== prevWidgets.variables[i]?.data
            );

            const updatedGauges = prevWidgets.gauges?.map(gauge => {
                const updatedData = getVariableById(gauge.data?.id);
                if (updatedData) return {...gauge, data: updatedData};
                return gauge;
            });

            const hasGaugeChanges = updatedGauges?.some((g, i) => g.data !== prevWidgets.gauges[i]?.data);

            if (hasChanges || hasGaugeChanges) {
                return {
                    ...prevWidgets,
                    variables: updatedVariables,
                    ...(hasGaugeChanges ? {gauges: updatedGauges} : {}),
                };
            }
            return prevWidgets;
        });
    }, [mqttData, getVariableById]);

    // Mantener vivos los badges de los widgets de dispositivo y de máquina.
    // Sin esto el número sale de la única petición REST del montaje y se queda
    // congelado, mientras los semáforos de las variables de al lado sí se
    // mueven por MQTT: en minutos el badge y sus hijos dejan de cuadrar.
    //
    // Depende de mqttData y NO de graphData: myindustrialdatagraph/{id} sólo
    // trae las variables del payload actual, mientras que myindustrialdata/{id}
    // las trae todas. Usar graphData reproduciría el bug del backend en el
    // navegador. Es el mismo patrón que ya usa la pantalla /devices.
    useEffect(() => {
        if (!mqttData || Object.keys(mqttData).length === 0) return;

        setWidgets(prevWidgets => {
            let changed = false;

            const updatedDevices = (prevWidgets.devices || []).map(widget => {
                const variables = mqttData[widget.data?.id];
                if (!Array.isArray(variables)) return widget;

                const counts = countAlertStates(variables);
                const data = widget.data;
                if (data.high_count === counts.high_count
                    && data.medium_count === counts.medium_count
                    && data.low_count === counts.low_count
                    && data.variables === variables) return widget;

                changed = true;
                return {
                    ...widget,
                    data: {
                        ...data,
                        high_count: counts.high_count,
                        medium_count: counts.medium_count,
                        low_count: counts.low_count,
                        variables: variables,
                        last_message: new Date().toISOString(),
                    },
                };
            });

            const updatedMachines = (prevWidgets.machines || []).map(widget => {
                const children = widget.children || [];
                if (children.length === 0) return widget;

                let touched = false;
                // Los hijos que todavía no publicaron conservan su aporte del
                // snapshot REST, así el total nunca queda incompleto.
                const nextChildren = children.map(child => {
                    const variables = mqttData[child.id];
                    if (!Array.isArray(variables)) return child;
                    const counts = countAlertStates(variables);
                    if (child.high_count === counts.high_count
                        && child.medium_count === counts.medium_count
                        && child.low_count === counts.low_count) return child;
                    touched = true;
                    return {
                        ...child,
                        high_count: counts.high_count,
                        medium_count: counts.medium_count,
                        low_count: counts.low_count,
                    };
                });
                if (!touched) return widget;

                changed = true;
                const total = nextChildren.reduce((acc, child) => ({
                    high_count: acc.high_count + child.high_count,
                    medium_count: acc.medium_count + child.medium_count,
                    low_count: acc.low_count + child.low_count,
                }), {high_count: 0, medium_count: 0, low_count: 0});

                return {...widget, children: nextChildren, data: {...widget.data, ...total}};
            });

            if (!changed) return prevWidgets;
            return {...prevWidgets, devices: updatedDevices, machines: updatedMachines};
        });
    }, [mqttData]);

    // Actualizar gráficos Y variables cuando llegan nuevos puntos por MQTT
    useEffect(() => {
        if (!graphData || Object.keys(graphData).length === 0) return;

        setWidgets(prevWidgets => {
            let hasChanges = false;

            // 1. Actualizar variables con los nuevos valores y estados del semáforo
            let updatedVariables = prevWidgets.variables || [];
            for (const deviceId in graphData) {
                const devicePoints = graphData[deviceId] || [];
                devicePoints.forEach(point => {
                    updatedVariables = updatedVariables.map(variable => {
                        if (variable.data?.id === point.variable_data_id) {
                            hasChanges = true;
                            return {
                                ...variable,
                                data: {
                                    ...variable.data,
                                    value: point.value,
                                    high_active: point.high_active,
                                    medium_active: point.medium_active,
                                    low_active: point.low_active,
                                }
                            };
                        }
                        return variable;
                    });
                });
            }

            // 2. Actualizar gráficos
            let updatedGraphs = prevWidgets.graphs || [];
            if (updatedGraphs.length > 0) {
                updatedGraphs = updatedGraphs.map(graph => {
                    const newPoints = [];

                    for (const deviceId in graphData) {
                        const devicePoints = graphData[deviceId] || [];
                        devicePoints.forEach(point => {
                            if (point.variable_data_id === graph.key_1_pk ||
                                point.variable_data_id === graph.key_2_pk ||
                                point.variable_data_id === graph.key_3_pk) {
                                newPoints.push(point);
                            }
                        });
                    }

                    if (newPoints.length > 0) {
                        hasChanges = true;
                        const currentData = graph.data || [];
                        const pointsByTime = {};
                        newPoints.forEach(point => {
                            const timeKey = point.x.substring(0, 19);
                            if (!pointsByTime[timeKey]) {
                                pointsByTime[timeKey] = { x: point.x };
                            }
                            if (point.variable_data_id === graph.key_1_pk) {
                                pointsByTime[timeKey].key_1 = parseFloat(point.value);
                            } else if (point.variable_data_id === graph.key_2_pk) {
                                pointsByTime[timeKey].key_2 = parseFloat(point.value);
                            } else if (point.variable_data_id === graph.key_3_pk) {
                                pointsByTime[timeKey].key_3 = parseFloat(point.value);
                            }
                        });

                        const newDataPoints = Object.values(pointsByTime);
                        const updatedData = [...currentData, ...newDataPoints];
                        const limitedData = updatedData.slice(-100);

                        return { ...graph, data: limitedData };
                    }
                    return graph;
                });
            }

            // 3. Actualizar alarm_tables con nuevos valores
            let updatedAlarmTables = prevWidgets.alarm_tables || [];
            if (updatedAlarmTables.length > 0) {
                updatedAlarmTables = updatedAlarmTables.map(alarmTable => {
                    let tableChanged = false;
                    const updatedVars = (alarmTable.variables || []).map(variable => {
                        for (const deviceId in graphData) {
                            const devicePoints = graphData[deviceId] || [];
                            const matchingPoint = devicePoints.find(p => p.variable_data_id === variable.id);
                            if (matchingPoint) {
                                tableChanged = true;
                                return {
                                    ...variable,
                                    value: matchingPoint.value,
                                    high_active: matchingPoint.high_active,
                                    medium_active: matchingPoint.medium_active,
                                    low_active: matchingPoint.low_active,
                                };
                            }
                        }
                        return variable;
                    });
                    if (tableChanged) {
                        hasChanges = true;
                        return {...alarmTable, variables: updatedVars};
                    }
                    return alarmTable;
                });
            }

            if (hasChanges) {
                return {
                    ...prevWidgets,
                    variables: updatedVariables,
                    graphs: updatedGraphs,
                    alarm_tables: updatedAlarmTables,
                };
            }
            return prevWidgets;
        });

        clearGraphData();
    }, [graphData, clearGraphData]);

    // Actualizar gráficos y widgets de variables calculadas (Custom Variables) cuando llegan por MQTT
    useEffect(() => {
        if (!customVarData || customVarData.length === 0) return;

        setWidgets(prevWidgets => {
            let hasGraphChanges = false;
            let hasVariableChanges = false;

            // 1. Actualizar gráficos con custom variables
            let updatedGraphs = prevWidgets.graphs || [];
            if (prevWidgets.graphs && prevWidgets.graphs.length > 0) {
                updatedGraphs = prevWidgets.graphs.map(graph => {
                    const newPoints = [];

                    customVarData.forEach(point => {
                        const customVarKey = `result_${point.custom_variable_id}`;
                        if (graph.key_1_pk === customVarKey ||
                            graph.key_2_pk === customVarKey ||
                            graph.key_3_pk === customVarKey) {
                            newPoints.push({
                                ...point,
                                matchedKey: graph.key_1_pk === customVarKey ? 'key_1' :
                                           graph.key_2_pk === customVarKey ? 'key_2' : 'key_3'
                            });
                        }
                    });

                    if (newPoints.length > 0) {
                        hasGraphChanges = true;
                        const currentData = graph.data || [];

                        const pointsByTime = {};
                        newPoints.forEach(point => {
                            const timeKey = point.x.substring(0, 19);
                            if (!pointsByTime[timeKey]) {
                                pointsByTime[timeKey] = { x: point.x };
                            }
                            pointsByTime[timeKey][point.matchedKey] = parseFloat(point.value);
                        });

                        const newDataPoints = Object.values(pointsByTime);
                        const updatedData = [...currentData, ...newDataPoints];
                        const limitedData = updatedData.slice(-100);

                        return {
                            ...graph,
                            data: limitedData
                        };
                    }
                    return graph;
                });
            }

            // 2. Actualizar widgets de variables individuales que son custom variables
            let updatedVariables = prevWidgets.variables || [];
            if (prevWidgets.variables && prevWidgets.variables.length > 0) {
                updatedVariables = prevWidgets.variables.map(variable => {
                    if (!variable.is_custom_variable) return variable;

                    const matchingPoint = customVarData.find(point =>
                        point.custom_variable_id === variable.data?.operation_id ||
                        point.custom_variable_id === variable.data?.id
                    );

                    if (matchingPoint) {
                        hasVariableChanges = true;
                        return {
                            ...variable,
                            data: {
                                ...variable.data,
                                value: matchingPoint.value,
                                high_active: matchingPoint.high_active,
                                medium_active: matchingPoint.medium_active,
                                low_active: matchingPoint.low_active,
                            }
                        };
                    }
                    return variable;
                });
            }

            if (hasGraphChanges || hasVariableChanges) {
                return {
                    ...prevWidgets,
                    graphs: updatedGraphs,
                    variables: updatedVariables
                };
            }
            return prevWidgets;
        });

        clearCustomVarData();
    }, [customVarData, clearCustomVarData]);

    useEffect(() => {
        localStorage.removeItem('variable');
        localStorage.removeItem('variable_id');
        axiosInstance.get(`dashboard/${dashboard_id}/`).then((response) => {
            setWidgets(response.data);
        });

        axiosInstance.get('machine/').then((response) => {
            setMachineOptions(response.data);
        });

        // Cargar variables de resultado de operaciones para el modal Join Graphs
        axiosInstance.get(`operation_result_variables/?project_id=${project_id}`).then((response) => {
            setResultVariables(response.data || []);
        }).catch((err) => {
            console.log('No result variables endpoint available yet:', err);
            setResultVariables([]);
        });
    }, [dashboard_id, project_id]);

    const saveGraph = () => {
        axiosInstance.post('graphs/', {
            pk: selectedGraph,
            graph_type: form.graph_type
        }).then((response) => {
            window.location.reload();
        });
    }

    useEffect(() => {
        const updateLayout = () => {
            const width = spaceRef.current ? spaceRef.current.offsetWidth : 0;
            const cols = Math.floor(width / 50); // Adjust this value as needed
            setAvailableSpace(width);
            setAvailableCols(cols);
        };

        // Update layout initially and on window resize
        window.addEventListener('resize', updateLayout);
        updateLayout();

        // Clean up the event listener when component unmounts
        return () => {
            window.removeEventListener('resize', updateLayout);
        };
    }, []);

    const [timer, setTimer] = useState(null);
    const [foundDevices, setFoundDevices] = useState([]);
    const [allDevices, setAllDevices] = useState([]);

    // Inicializar form con los datos del gráfico existente cuando se abre el modal Join Graphs
    useEffect(() => {
        if (joinGraphModal && widgetData?.type === 'graph' && widgetData?.data) {
            const graphData = widgetData.data;
            // Inicializar completamente con datos del gráfico actual (sin mezclar con form anterior)
            setForm({
                pk: graphData.pk,
                widget_name: graphData.widget_name,
                widget_sampling: graphData.widget_sampling,
                search_field: '',
                // Mapear key_X_pk del backend al form para que se envíen los IDs correctos
                key_1_pk: graphData.key_1_pk || null,
                key_2_pk: graphData.key_2_pk || null,
                key_3_pk: graphData.key_3_pk || null,
                // Usar los nombres del backend si existen
                key_1_name: graphData.key_1_name || (graphData.key_1 ? `Variable: ${graphData.key_1}` : ''),
                key_2_name: graphData.key_2_name || (graphData.key_2 ? `Variable: ${graphData.key_2}` : ''),
                key_3_name: graphData.key_3_name || (graphData.key_3 ? `Variable: ${graphData.key_3}` : ''),
            });
        }
    }, [joinGraphModal, widgetData]);

    // Cargar devices cuando se abre el modal Alarm Table
    useEffect(() => {
        if (showAlarmTableModal && alarmTableDevices.length === 0) {
            axiosInstance.get(`device/?project_id=${project_id}`).then((response) => {
                setAlarmTableDevices(response.data);
            });
        }
    }, [showAlarmTableModal, project_id, alarmTableDevices.length]);

    // Cargar todos los devices cuando se abre el modal Join Graphs
    useEffect(() => {
        if (joinGraphModal && allDevices.length === 0) {
            axiosInstance.get(`device/?project_id=${project_id}`).then((response) => {
                setAllDevices(response.data);
                setFoundDevices(response.data);
            });
        }
    }, [joinGraphModal, project_id, allDevices.length]);

    // Filtrar devices y variables en el frontend
    useEffect(() => {
        // Limpiar timer anterior si existe
        if (timer) {
            clearTimeout(timer);
        }

        const newTimer = setTimeout(() => {
            if (!form['search_field'] || form['search_field'].trim() === '') {
                setFoundDevices(allDevices);
                return;
            }

            const searchTerm = form['search_field'].toLowerCase();
            const filtered = allDevices.map(device => {
                // Filtrar variables que coincidan con la búsqueda
                const matchingVariables = device.variables?.filter(variable => {
                    const deviceName = device.name.toLowerCase();
                    const variableName = variable.variable?.name?.toLowerCase() || '';
                    return deviceName.includes(searchTerm) || variableName.includes(searchTerm);
                }) || [];

                // Si el device tiene variables que coinciden, retornar el device con solo esas variables
                if (matchingVariables.length > 0) {
                    return {
                        ...device,
                        variables: matchingVariables
                    };
                }

                // Si el nombre del device coincide, retornar todas sus variables
                if (device.name.toLowerCase().includes(searchTerm)) {
                    return device;
                }

                return null;
            }).filter(device => device !== null);

            setFoundDevices(filtered);
        }, 400);

        setTimer(newTimer);

        // Cleanup function para limpiar el timer cuando el componente se desmonte
        return () => {
            if (newTimer) {
                clearTimeout(newTimer);
            }
        };
    }, [form['search_field'], allDevices]);

    const joinGraphs = () => {
        // Detectar si cada key es una variable de resultado (prefijo "result_")
        const isCustom1 = String(form.key_1_pk || '').startsWith('result_');
        const isCustom2 = String(form.key_2_pk || '').startsWith('result_');
        const isCustom3 = String(form.key_3_pk || '').startsWith('result_');

        const payload = {
            ...form,
            is_custom_variable_1: isCustom1,
            is_custom_variable_2: isCustom2,
            is_custom_variable_3: isCustom3,
        };

        axiosInstance.post(`dashboard/${form.pk}/join_graph/`, payload).then((response) => {
            window.location.reload();
        });
    }

    const saveLayout = (exitEditAfter) => {
        if(joinGraphModal) {
            joinGraphs();
        } else {
            axiosInstance.post(`dashboard/${dashboard_id}/layout/`, layout).then(() => {
                // Update widget positions in state so they don't revert
                setWidgets(prev => {
                    const layoutMap = {};
                    layout.forEach(item => { layoutMap[item.i] = item; });

                    const updatePositions = (items, keyFn) => items?.map(item => {
                        const key = String(keyFn(item));
                        const l = layoutMap[key];
                        if (l) return {...item, x: l.x, y: l.y, w: l.w, h: l.h};
                        return item;
                    });

                    return {
                        ...prev,
                        variables: updatePositions(prev.variables, v => v.widget_pk),
                        machines: updatePositions(prev.machines, m => m.widget_pk),
                        devices: updatePositions(prev.devices, d => d.widget_pk),
                        graphs: updatePositions(prev.graphs, g => g.pk + 1000000000),
                        bobs: updatePositions(prev.bobs, b => b.widget_pk + 2000000000),
                        alarm_tables: updatePositions(prev.alarm_tables, a => a.widget_pk + 3000000000),
                        production_histograms: updatePositions(prev.production_histograms, h => h.widget_pk + 4000000000),
                        gauges: updatePositions(prev.gauges, g => g.widget_pk + 5000000000),
                        buttons: updatePositions(prev.buttons, b => b.widget_pk + 6000000000),
                    };
                });
                setHasLayoutChanges(false);
                setInitialLayout([...layout]);
                if (exitEditAfter) setEditMode(false);
            });
        }
    }

    const handleOutsideClick = (event) => {
        if (popupRef.current && !popupRef.current.contains(event.target)) {
            setShowPopup(false);
        }
    };

    useEffect(() => {
        if (showPopup) {
            document.addEventListener('mousedown', handleOutsideClick);
        } else {
            document.removeEventListener('mousedown', handleOutsideClick);
        }

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [showPopup]);

    const saveSettings = () => {
        let formData = new FormData();
        for(const key in form) {
            formData.append(key, form[key]);
        }

        if(widgetData.type === 'machine') {
            axiosInstance.put('device_user/' + form.id + '/', formData).then((res) => {
                window.location.reload();
            });
        } else if(widgetData.type === 'device') {
            axiosInstance.put('device/' + form.id + '/', formData).then((res) => {
                window.location.reload();
            });
        } else if(widgetData.type === 'variable') {
            if (widgetData.is_custom_variable) {
                // Custom variables usan el endpoint custom_variable
                axiosInstance.put('custom_variable/' + form.id + '/', {
                    precision: form.precision,
                    low_threshold: form.green_1,
                    medium_threshold: form.yellow_1,
                    low_threshold_2: form.green_2,
                    medium_threshold_2: form.yellow_2,
                    disable_alerts: form.disable_alerts || false,
                    bool_alert_value: form.bool_alert_value,
                    bool_alert_type: form.bool_alert_type,
                }).then(() => {
                    window.location.reload();
                });
            } else {
                axiosInstance.put('variable_data/' + form.id + '/', form).then(() => {
                    window.location.reload();
                });
            }
        }
    }

    return (
        <div className={styles.dashboard_container}>
            <Header/>

            {showPopup &&
                <div ref={popupRef} className={styles.popUpModule} style={{top: mousePosition.y + 10, left: mousePosition.x, position: 'absolute', zIndex: '100000'}}>
                    <ActionPopup setShowPopup={setShowPopup} widget_pk={selectedWidget} widgetData={widgetData} setShowMachineModal={setShowMachineModal} setShowDeviceModal={setShowDeviceModal} setShowVariableModal={setShowVariableModal} setJoinGraphModal={setJoinGraphModal} setShowAlarmTableModal={setShowAlarmTableModal} onOpenAlarmTableSettings={(atData) => {
                        setAlarmTableName(atData.widget_name || '');
                        setAlarmTableSelectedDevice(atData.device?.id || null);
                        setAlarmTableSelectedVarIds(atData.variable_ids || []);
                        setEditingAlarmTableWidgetPk(atData.widget_pk || null);
                        if (atData.device?.id) {
                            axiosInstance.get(`variable_data/?device_id=${atData.device.id}`).then((res) => {
                                setAlarmTableVariables(res.data || []);
                            });
                        }
                    }}/>
                </div>
            }

            <Modal name={t('dashboard.editWidget')} show={showMachineModal} setter={setShowMachineModal} buttonName={t('common.save')} onClick={saveSettings}>
                <LogoInput name={'Machine Logo'} current={form?.image?.image} internalName={'logo'} form={form} setForm={setForm} hideName={true}/>
                <TextInput name={'name'} internalName={'name'} form={form} setForm={setForm}/>
            </Modal>

            <Modal name={t('dashboard.editWidget')} show={showDeviceModal} setter={setShowDeviceModal} buttonName={t('common.save')} onClick={saveSettings}>
                <LogoInput name={'Device Logo'} current={form?.image?.image} internalName={'logo'} form={form} setForm={setForm} hideName={true}/>
                <TextInput name={'name'} internalName={'name'} form={form} setForm={setForm}/>
                <TextInput name={'Device EUI'} internalName={'sn'} form={form} setForm={setForm}/>
                <Label>Machine</Label>
                <Select options={machineOptions.map((x) => {
                    return {name: x.name, value: x.id}
                })} value={form.device_user_id} onChange={(value) => {
                    setForm({
                        ...form,
                        device_user_id: value
                    })
                }}/>
            </Modal>

            <Modal name={t('dashboard.editWidget')} show={showVariableModal} setter={setShowVariableModal} buttonName={t('common.save')} onClick={saveSettings}>
                <div className={`mb-4`}>
                    {/* Get variable type from widgetData - con detección automática */}
                    {(() => {
                        let varType = Number(widgetData?.data?.variable?.type);

                        // Si el tipo es 0 o undefined, detectar automáticamente del valor
                        if (varType === 0 || isNaN(varType)) {
                            const rawValue = widgetData?.data?.value;
                            const strValue = String(rawValue).toLowerCase().trim();
                            // Para custom variables, también verificar data_type
                            const dataType = widgetData?.data?.data_type;

                            if (dataType === 'boolean' ||
                                rawValue === 'True' || rawValue === 'False' ||
                                rawValue === true || rawValue === false ||
                                strValue === 'true' || strValue === 'false' ||
                                strValue === '1' || strValue === '0' ||
                                strValue === '1.0' || strValue === '0.0') {
                                varType = 5; // BOOLEAN
                            } else if (dataType === 'string' || (typeof rawValue === 'string' && isNaN(parseFloat(rawValue)))) {
                                varType = 3; // STRING
                            } else if (!isNaN(parseFloat(rawValue))) {
                                varType = 2; // FLOAT
                            }
                        }

                        const isBoolean = varType === 5;
                        const isString = varType === 3;
                        // Etiquetas heredadas del tipo de dispositivo, para el placeholder
                        const inheritedLabels = getStateLabels({variable: widgetData?.data?.variable});

                        return (
                            <>
                                {/* Decimal precision - only for numeric types */}
                                {!isBoolean && !isString && (
                                    <>
                                        <div><b>Decimal Precision</b></div>
                                        <div style={{fontSize: "x-large"}}><b>{form.precision}</b></div>
                                        <SliderInput
                                            min={0}
                                            max={4}
                                            step={1}
                                            name={'precision'}
                                            form={form}
                                            setForm={setForm}
                                        />
                                        <div className={'mt-4 mb-4'} style={{height: "1px", backgroundColor: "#CBD5E1"}}/>
                                    </>
                                )}

                                {/* String type has no limit configuration */}
                                {isString && (
                                    <div style={{padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '8px', marginBottom: '16px'}}>
                                        <div style={{color: '#0369a1', fontWeight: 500}}>
                                            String variables do not have limit configuration.
                                        </div>
                                    </div>
                                )}

                                {/* Disable alerts - not for strings */}
                                {!isString && (
                                    <div className={'mb-3'}>
                                        <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                                            <input
                                                type="checkbox"
                                                checked={form.disable_alerts || false}
                                                onChange={(e) => setForm({...form, disable_alerts: e.target.checked})}
                                                style={{marginRight: '8px', width: '18px', height: '18px'}}
                                            />
                                            <span><b>Disable Alerts</b> (hide traffic light)</span>
                                        </label>
                                    </div>
                                )}

                                {/* Etiquetas de estado. Igual que en variable.js:
                                    depende sólo de isBoolean, nunca de disable_alerts. */}
                                {isBoolean && (
                                    <div className={'mb-3'}>
                                        <div><b>{t('variables.stateLabels')}</b></div>
                                        <div style={{marginBottom: '8px', color: '#64748b', fontSize: '13px'}}>
                                            {t('variables.stateLabelsHint')}
                                        </div>
                                        <div style={{display: 'flex', gap: '12px'}}>
                                            <div style={{flex: 1}}>
                                                <TextInput name={t('variables.trueLabel')} internalName={'true_label'}
                                                           placeholder={inheritedLabels.trueLabel}
                                                           form={form} setForm={setForm}/>
                                            </div>
                                            <div style={{flex: 1}}>
                                                <TextInput name={t('variables.falseLabel')} internalName={'false_label'}
                                                           placeholder={inheritedLabels.falseLabel}
                                                           form={form} setForm={setForm}/>
                                            </div>
                                        </div>
                                        <div className={'mt-4 mb-4'} style={{height: "1px", backgroundColor: "#CBD5E1"}}/>
                                    </div>
                                )}

                                {/* Boolean limit configuration */}
                                {isBoolean && !form.disable_alerts && (
                                    <div>
                                        <div><b>Boolean Alert Configuration</b></div>
                                        <div style={{marginTop: '12px'}}>
                                            <div style={{marginBottom: '8px', color: '#64748b'}}>
                                                Select which value triggers an alert:
                                            </div>
                                            <div style={{display: 'flex', gap: '12px', marginBottom: '16px'}}>
                                                <label style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '8px 16px',
                                                    border: form.bool_alert_value === true ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    backgroundColor: form.bool_alert_value === true ? '#eff6ff' : '#fff'
                                                }}>
                                                    <input
                                                        type="radio"
                                                        name="bool_alert_value"
                                                        checked={form.bool_alert_value === true}
                                                        onChange={() => setForm({...form, bool_alert_value: true, bool_alert_type: form.bool_alert_type || 1})}
                                                        style={{marginRight: '8px'}}
                                                    />
                                                    True = Alert
                                                </label>
                                                <label style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '8px 16px',
                                                    border: form.bool_alert_value === false ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    backgroundColor: form.bool_alert_value === false ? '#eff6ff' : '#fff'
                                                }}>
                                                    <input
                                                        type="radio"
                                                        name="bool_alert_value"
                                                        checked={form.bool_alert_value === false}
                                                        onChange={() => setForm({...form, bool_alert_value: false, bool_alert_type: form.bool_alert_type || 1})}
                                                        style={{marginRight: '8px'}}
                                                    />
                                                    False = Alert
                                                </label>
                                                <label style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '8px 16px',
                                                    border: form.bool_alert_value === null || form.bool_alert_value === undefined ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    backgroundColor: form.bool_alert_value === null || form.bool_alert_value === undefined ? '#eff6ff' : '#fff'
                                                }}>
                                                    <input
                                                        type="radio"
                                                        name="bool_alert_value"
                                                        checked={form.bool_alert_value === null || form.bool_alert_value === undefined}
                                                        onChange={() => setForm({...form, bool_alert_value: null, bool_alert_type: null})}
                                                        style={{marginRight: '8px'}}
                                                    />
                                                    No Alert
                                                </label>
                                            </div>

                                            {form.bool_alert_value !== null && form.bool_alert_value !== undefined && (
                                                <div>
                                                    <div style={{marginBottom: '8px', color: '#64748b'}}>
                                                        Alert type:
                                                    </div>
                                                    <div style={{display: 'flex', gap: '12px'}}>
                                                        <label style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            padding: '8px 16px',
                                                            border: form.bool_alert_type === 1 ? '2px solid #ef4444' : '1px solid #e2e8f0',
                                                            borderRadius: '8px',
                                                            cursor: 'pointer',
                                                            backgroundColor: form.bool_alert_type === 1 ? '#fef2f2' : '#fff'
                                                        }}>
                                                            <input
                                                                type="radio"
                                                                name="bool_alert_type"
                                                                checked={form.bool_alert_type === 1}
                                                                onChange={() => setForm({...form, bool_alert_type: 1})}
                                                                style={{marginRight: '8px'}}
                                                            />
                                                            <span style={{color: '#ef4444', fontWeight: 600}}>Danger</span>
                                                        </label>
                                                        <label style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            padding: '8px 16px',
                                                            border: form.bool_alert_type === 2 ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                                                            borderRadius: '8px',
                                                            cursor: 'pointer',
                                                            backgroundColor: form.bool_alert_type === 2 ? '#fffbeb' : '#fff'
                                                        }}>
                                                            <input
                                                                type="radio"
                                                                name="bool_alert_type"
                                                                checked={form.bool_alert_type === 2}
                                                                onChange={() => setForm({...form, bool_alert_type: 2})}
                                                                style={{marginRight: '8px'}}
                                                            />
                                                            <span style={{color: '#f59e0b', fontWeight: 600}}>Warning</span>
                                                        </label>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Numeric threshold limits - only for numeric types */}
                                {!isBoolean && !isString && !form.disable_alerts && (
                                    <div>
                                        <div><b>Threshold Limits</b></div>
                                        <VerticalInput form={form} setForm={setForm}/>
                                    </div>
                                )}
                            </>
                        );
                    })()}
                </div>
            </Modal>

            <Modal name={t('dashboard.editWidget')} show={joinGraphModal} setter={setJoinGraphModal} buttonName={t('common.save')} onClick={joinGraphs}>
                <div>
                    <TextInput form={form} setForm={setForm} name={'Name'} internalName={'widget_name'}/>
                    <SelectInput form={form} setForm={setForm} name={'Sampling'} internalName={'widget_sampling'}
                                 options={[{id: 0, name: '1 hour'}, {id: 1, name: '24 hours'}, {id: 2, name: '7 days'}]}
                    />

                    <div className={'mt-3'}/>

                    <div>Variable 1: {form['key_1_name']}</div>
                    <div>Variable 2: {form['key_2_name']}</div>
                    <div className={'mb-3'}>Variable 3: {form['key_3_name']}</div>
                    <TextInput form={form} setForm={setForm} name={'Search Device'} internalName={'search_field'}/>
                    {foundDevices.map((device, deviceIndex) => (
                        device.variables?.map((item, index) => {
                            const itemId = item.id;
                            const itemName = device.name + " - " + item.variable.name;
                            return (
                                <div key={index} className={'d-flex mb-2 px-2 py-2 align-items-center justify-content-between'} style={{borderRadius: '6px', border: '1px #ccc solid'}}>
                                    <div>{device.name} - {item.variable.name}</div>
                                    <div>
                                        <FontAwesomeIcon style={{cursor: 'pointer'}} onClick={() => {
                                            setForm(prevForm => ({...prevForm, key_1_pk: itemId, key_1_name: itemName}));
                                        }} icon={fa1} color={'#4F46E5'} className={'me-3'}/>
                                        <FontAwesomeIcon style={{cursor: 'pointer'}} onClick={() => {
                                            setForm(prevForm => ({...prevForm, key_2_pk: itemId, key_2_name: itemName}));
                                        }} icon={fa2} color={'#4F46E5'} className={'me-3'}/>
                                        <FontAwesomeIcon style={{cursor: 'pointer'}} onClick={() => {
                                            setForm(prevForm => ({...prevForm, key_3_pk: itemId, key_3_name: itemName}));
                                        }} icon={fa3} color={'#4F46E5'} className={'me-2'}/>
                                    </div>
                                </div>
                            );
                        })
                    ))}

                    {/* Sección de Variables de Resultado de Operaciones */}
                    {resultVariables.length > 0 && (
                        <>
                            <div className="mt-3 mb-2 fw-bold" style={{color: '#722ED1'}}>Result Variables (from Operations)</div>
                            {resultVariables
                                .filter((resultVar) => {
                                    // Filtrar por el campo de búsqueda
                                    if (!form['search_field']) return true;
                                    return resultVar.name.toLowerCase().includes(form['search_field'].toLowerCase());
                                })
                                .map((resultVar) => {
                                // Asegurar que el ID tenga el prefijo "result_" para diferenciarlo de variables de device
                                const resultId = String(resultVar.id).startsWith('result_') ? resultVar.id : `result_${resultVar.id}`;
                                const resultName = resultVar.name;
                                return (
                                    <div key={resultVar.id} className={'d-flex mb-2 px-2 py-2 align-items-center justify-content-between'} style={{borderRadius: '6px', border: '1px #D3ADF7 solid', backgroundColor: '#F9F0FF'}}>
                                        <div style={{color: '#722ED1'}}>{resultVar.name}</div>
                                        <div>
                                            <FontAwesomeIcon style={{cursor: 'pointer'}} onClick={() => {
                                                setForm(prevForm => ({...prevForm, key_1_pk: resultId, key_1_name: resultName}));
                                            }} icon={fa1} color={'#722ED1'} className={'me-3'}/>
                                            <FontAwesomeIcon style={{cursor: 'pointer'}} onClick={() => {
                                                setForm(prevForm => ({...prevForm, key_2_pk: resultId, key_2_name: resultName}));
                                            }} icon={fa2} color={'#722ED1'} className={'me-3'}/>
                                            <FontAwesomeIcon style={{cursor: 'pointer'}} onClick={() => {
                                                setForm(prevForm => ({...prevForm, key_3_pk: resultId, key_3_name: resultName}));
                                            }} icon={fa3} color={'#722ED1'} className={'me-2'}/>
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            </Modal>

            <Modal name={t('dashboard.editWidget')} show={showAlarmTableModal} setter={setShowAlarmTableModal} buttonName={t('common.save')} onClick={() => {
                if (editingAlarmTableWidgetPk) {
                    axiosInstance.patch(`dashboard_widget/${editingAlarmTableWidgetPk}/`, {
                        name: alarmTableName || 'Alarm Table',
                        key: alarmTableSelectedDevice,
                        variable_ids: alarmTableSelectedVarIds,
                    }).then(() => {
                        window.location.reload();
                    });
                } else {
                    axiosInstance.post(`dashboard/${dashboard_id}/add_widget/`, {
                        name: alarmTableName || 'Alarm Table',
                        key: alarmTableSelectedDevice,
                        type: 5,
                        variable_ids: alarmTableSelectedVarIds,
                    }).then(() => {
                        window.location.reload();
                    });
                }
            }}>
                <div className={'mb-3'}>
                    <TextInput name={'Widget Name'} internalName={'alarm_table_name'} form={{alarm_table_name: alarmTableName}} setForm={(f) => setAlarmTableName(f.alarm_table_name)}/>
                </div>
                <div className={'mb-3'}>
                    <label style={{fontWeight: 500, marginBottom: '4px', display: 'block'}}>Device</label>
                    <select
                        className="form-select"
                        value={alarmTableSelectedDevice || ''}
                        onChange={(e) => {
                            const deviceId = parseInt(e.target.value);
                            setAlarmTableSelectedDevice(deviceId);
                            setAlarmTableSelectedVarIds([]);
                            if (deviceId) {
                                axiosInstance.get(`variable_data/?device_id=${deviceId}`).then((res) => {
                                    setAlarmTableVariables(res.data || []);
                                });
                            } else {
                                setAlarmTableVariables([]);
                            }
                        }}
                    >
                        <option value="">Select a device...</option>
                        {alarmTableDevices.map((device) => (
                            <option key={device.id} value={device.id}>{device.name}</option>
                        ))}
                    </select>
                </div>
                {alarmTableVariables.length > 0 && (
                    <div>
                        <label style={{fontWeight: 500, marginBottom: '4px', display: 'block'}}>Select Variables</label>
                        <div style={{maxHeight: '300px', overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '8px'}}>
                            {alarmTableVariables.map((variable) => (
                                <label key={variable.id} style={{display: 'flex', alignItems: 'center', padding: '6px 4px', cursor: 'pointer', borderBottom: '1px solid #F1F5F9'}}>
                                    <input
                                        type="checkbox"
                                        checked={alarmTableSelectedVarIds.includes(variable.id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setAlarmTableSelectedVarIds(prev => [...prev, variable.id]);
                                            } else {
                                                setAlarmTableSelectedVarIds(prev => prev.filter(id => id !== variable.id));
                                            }
                                        }}
                                        style={{marginRight: '8px', width: '16px', height: '16px'}}
                                    />
                                    <span>{variable.variable?.name || `Variable ${variable.id}`}</span>
                                    <span style={{marginLeft: 'auto', color: '#94A3B8', fontSize: '12px'}}>
                                        {variable.value != null ? variable.value : '--'}
                                    </span>
                                </label>
                            ))}
                        </div>
                        <div style={{marginTop: '8px', color: '#64748B', fontSize: '12px'}}>
                            {alarmTableSelectedVarIds.length} variable(s) selected
                        </div>
                    </div>
                )}
            </Modal>

            <Modal name={t('dashboard.settings')} show={showGraphicsModal} setter={setShowGraphicsModal} buttonName={t('common.save')} onClick={saveGraph}>
                <div className={'mb-3'}>
                    <SelectInput name={'Graphic Type'} internalName={'graph_type'} options={[{id: 0, name: 'Line'}, {id: 1, name:'Bar'}, {id: 2, name: 'Area'}, {id: 3, name: 'Radar'}, {id: 4, name: 'Pie'}, {id: 5, name: 'Step (Boolean)'}, {id: 6, name: 'Timeline (String)'}]} form={form} setForm={setForm}/>
                </div>
            </Modal>

            {editMode && (
                <div
                    className={styles.editButton}
                    style={{bottom: '80px', backgroundColor: '#FFF'}}
                    onClick={() => {
                        setAlarmTableSelectedDevice(null);
                        setAlarmTableVariables([]);
                        setAlarmTableSelectedVarIds([]);
                        setAlarmTableName('');
                        setEditingAlarmTableWidgetPk(null);
                        setShowAlarmTableModal(true);
                    }}
                >
                    <FontAwesomeIcon icon={faTableCells}/>
                    {t('dashboard.addWidget')}
                </div>
            )}

            <div
                className={`${styles.editButton} ${editMode ? styles.editButtonActive : ''}`}
                onClick={() => {
                    if (editMode && (hasLayoutChanges || joinGraphModal)) {
                        saveLayout(true);
                    } else {
                        setEditMode(!editMode);
                    }
                }}
            >
                <FontAwesomeIcon icon={editMode ? faLockOpen : faLock}/>
                {editMode ? t('dashboard.doneEditing') : t('dashboard.editDashboard')}
            </div>

            <div className={`${styles.dashboardInnerContainer} mt-3`} ref={spaceRef} style={{overflowX: 'hidden', overflowY: 'hidden'}}>
                {Object.keys(widgets).length > 0 && (widgets?.machines?.length > 0 || widgets?.devices?.length > 0 || widgets?.variables?.length > 0 || widgets?.graphs?.length > 0 || widgets?.bobs?.length > 0 || widgets?.alarm_tables?.length > 0 || widgets?.production_histograms?.length > 0 || widgets?.gauges?.length > 0 || widgets?.buttons?.length > 0) ?
                    <GridLayout
                        cols={availableCols}
                        rowHeight={26}
                        width={availableSpace}
                        height={'100%'}
                        isDraggable={editMode}
                        isResizable={false}
                        compactType={null}
                        preventCollision={true}
                        onLayoutChange={(newLayout) => {
                            setLayout(newLayout);
                            // Solo marcar cambios después de que se establezca el layout inicial
                            if (initialLayout.length > 0) {
                                const hasChanges = newLayout.some((item, index) => {
                                    const initial = initialLayout.find(i => i.i === item.i);
                                    return initial && (initial.x !== item.x || initial.y !== item.y || initial.w !== item.w || initial.h !== item.h);
                                });
                                setHasLayoutChanges(hasChanges);
                            } else {
                                setInitialLayout(newLayout);
                            }
                        }}
                    >
                        {widgets.variables?.map((variable, index) => (
                            <div className={`${styles.dashboardWidget} ${editMode ? styles.editModeWidget : ''}`} onContextMenu={(event) => {
                                if(!editMode) return;
                                event.preventDefault();
                                setMousePosition({ x: event.clientX, y: event.clientY });
                                setShowPopup(true);
                                setSelectedWidget(variable.widget_pk);
                                setForm({
                                    ...variable.data,
                                    yellow_1: variable.data.medium_threshold,
                                    yellow_2: variable.data.medium_threshold_2,
                                    green_1: variable.data.low_threshold,
                                    green_2: variable.data.low_threshold_2,
                                });
                                setWidgetData({data: variable.data, type: "variable", is_custom_variable: variable.is_custom_variable});
                            }} key={variable.widget_pk} data-grid={{x: variable.x, y: variable.y, w: 8, h: 5}}>
                                <VariableBox name={variable?.widget_name} data={variable.data} key={index} isDashboardItem={true}/>
                            </div>
                        ))}

                        {widgets.machines?.map((machine, index) => (
                            <div className={`${styles.dashboardWidget} ${editMode ? styles.editModeWidget : ''}`} onContextMenu={(event) => {
                                if(!editMode) return;
                                event.preventDefault();
                                setMousePosition({ x: event.clientX, y: event.clientY });
                                setShowPopup(true);
                                setSelectedWidget(machine.widget_pk);
                                setForm(machine.data);
                                setWidgetData({data: machine.data, type: "machine"});
                            }} key={machine.widget_pk} data-grid={{x: machine.x, y: machine.y, w: 8, h: 5}}>
                                <DeviceBox name={machine?.widget_name} data={machine.data} notDevice={true} key={index} isDashboardItem={true} isDashboardEditMode={editMode}/>
                            </div>
                        ))}

                        {widgets.devices?.map((device, index) => (
                            <div className={`${styles.dashboardWidget} ${editMode ? styles.editModeWidget : ''}`} onContextMenu={(event) => {
                                if(!editMode) return;
                                event.preventDefault();
                                setMousePosition({ x: event.clientX, y: event.clientY });
                                setShowPopup(true);
                                setSelectedWidget(device.widget_pk);
                                setForm({
                                    ...device.data,
                                    device_user_id: [device.data?.device_user]
                                });
                                setWidgetData({data: device.data, type: "device"});
                            }} key={device.widget_pk} data-grid={{x:device.x, y:device.y, w: 8, h: 6}}>
                                <DeviceBox name={device?.widget_name} data={device.data} key={index} isDashboardItem={true} isDashboardEditMode={editMode}/>
                            </div>
                        ))}

                        {widgets.graphs?.map((data, index) => (
                            <div className={`${styles.dashboardWidget} ${editMode ? styles.editModeWidget : ''}`} onContextMenu={(event) => {
                                if(!editMode) return;
                                event.preventDefault();
                                setMousePosition({ x: event.clientX, y: event.clientY });
                                setShowPopup(true);
                                setSelectedWidget(data.pk);
                                setWidgetData({data: data, type: "graph"});
                                // No hacer setForm aquí - el useEffect de joinGraphModal lo manejará
                            }} key={data.pk + 1000000000} data-grid={{x: data.x, y: data.y, w: data.w || 11, h: data.h || 13, isResizable: editMode, resizeHandles: ['se']}} style={{width: '100%', height: '100%'}}>
                                <DashboardGraphic graph_name={data.widget_name} sampling={data.widget_sampling} data={data}/>
                            </div>
                        ))}

                        {widgets.bobs?.map((bob, index) => (
                            <div className={`${styles.dashboardWidget} ${editMode ? styles.editModeWidget : ''}`} onContextMenu={(event) => {
                                if(!editMode) return;
                                event.preventDefault();
                                setMousePosition({ x: event.clientX, y: event.clientY });
                                setShowPopup(true);
                                setSelectedWidget(bob.widget_pk);
                                setWidgetData({data: bob.data, type: "bob"});
                            }} key={bob.widget_pk + 2000000000} data-grid={{x: bob.x, y: bob.y, w: bob.w || 8, h: Math.max(bob.h || 0, 10)}}>
                                <BobWidget
                                    name={bob.widget_name}
                                    data={bob.data}
                                    bobMetrics={bob.bob_metrics}
                                    currentStatus={bob.current_status}
                                    unacknowledgedAlarms={bob.unacknowledged_alarms}
                                    isDashboardItem={true}
                                    isDashboardEditMode={editMode}
                                />
                            </div>
                        ))}

                        {widgets.alarm_tables?.map((alarmTable, index) => (
                            <div className={`${styles.dashboardWidget} ${editMode ? styles.editModeWidget : ''}`} onContextMenu={(event) => {
                                if(!editMode) return;
                                event.preventDefault();
                                setMousePosition({ x: event.clientX, y: event.clientY });
                                setShowPopup(true);
                                setSelectedWidget(alarmTable.widget_pk);
                                setWidgetData({data: alarmTable, type: "alarm_table"});
                            }} key={alarmTable.widget_pk + 3000000000} data-grid={{x: alarmTable.x, y: alarmTable.y, w: alarmTable.w || 10, h: alarmTable.h || 8}}>
                                <AlarmTableWidget
                                    widgetName={alarmTable.widget_name}
                                    device={alarmTable.device}
                                    variables={alarmTable.variables}
                                />
                            </div>
                        ))}

                        {widgets.production_histograms?.map((hist, index) => (
                            <div className={`${styles.dashboardWidget} ${editMode ? styles.editModeWidget : ''}`} onContextMenu={(event) => {
                                if(!editMode) return;
                                event.preventDefault();
                                setMousePosition({ x: event.clientX, y: event.clientY });
                                setShowPopup(true);
                                setSelectedWidget(hist.widget_pk);
                                setWidgetData({data: hist, type: "production_histogram"});
                            }} key={hist.widget_pk + 4000000000} data-grid={{x: hist.x, y: hist.y, w: hist.w || 11, h: hist.h || 10, isResizable: editMode, resizeHandles: ['se']}}>
                                <DashboardProductionHistogram
                                    variable_data_id={hist.variable_data_id}
                                    widget_name={hist.widget_name}
                                    sampling={hist.widget_sampling}
                                />
                            </div>
                        ))}

                        {widgets.gauges?.map((gauge, index) => (
                            <div className={`${styles.dashboardWidget} ${editMode ? styles.editModeWidget : ''}`} onContextMenu={(event) => {
                                if(!editMode) return;
                                event.preventDefault();
                                setMousePosition({ x: event.clientX, y: event.clientY });
                                setShowPopup(true);
                                setSelectedWidget(gauge.widget_pk);
                                setWidgetData({data: gauge, type: "gauge"});
                            }} key={gauge.widget_pk + 5000000000} data-grid={{x: gauge.x, y: gauge.y, w: gauge.w || 8, h: gauge.h || 6}}>
                                <GaugeWidget name={gauge.widget_name} data={gauge.data}/>
                            </div>
                        ))}

                        {widgets.buttons?.map((btn, index) => (
                            <div className={`${styles.dashboardWidget} ${editMode ? styles.editModeWidget : ''}`} onContextMenu={(event) => {
                                if(!editMode) return;
                                event.preventDefault();
                                setMousePosition({ x: event.clientX, y: event.clientY });
                                setShowPopup(true);
                                setSelectedWidget(btn.widget_pk);
                                setWidgetData({data: btn, type: "button"});
                            }} key={btn.widget_pk + 6000000000} data-grid={{x: btn.x, y: btn.y, w: btn.w || 4, h: btn.h || 3}}>
                                <ButtonWidget name={btn.widget_name} data={btn.data}/>
                            </div>
                        ))}
                    </GridLayout>
                    :
                    <EmptyPlaceholder message={t('dashboard.noWidgets')}/>
                }
            </div>
        </div>
    );
}

//https://www.npmjs.com/package/react-grid-layout/v/1.4.1

//<ChartComponent setShowModal={setShowGraphicsModal} data={data} chartType={getGraphType(data)} frequency={'1h'} isDashboardItem={true} setSelectedGraph={setSelectedGraph}/>